import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Calendar, Download, TrendingUp, Mail } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";

const AdvancedAnalytics = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start_date: "",
    end_date: ""
  });

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      toast.error("Failed to fetch project");
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start_date) params.append('start_date', dateRange.start_date);
      if (dateRange.end_date) params.append('end_date', dateRange.end_date);
      
      const response = await apiClient.get(`/analytics/${projectId}?${params.toString()}`);
      setAnalytics(response.data);
      toast.success("Analytics loaded successfully");
    } catch (error) {
      toast.error("Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  };

  const exportProjectSummary = async () => {
    try {
      const response = await apiClient.get(`/export/project-summary/${projectId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Project_Summary_${project?.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const sendDailyReport = async () => {
    try {
      const response = await apiClient.post(`/send-daily-report/${projectId}`);
      toast.success(`Report sent to ${response.data.recipients.length} recipients`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send report");
    }
  };

  if (!project) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">ADVANCED ANALYTICS</h1>
        <p className="text-slate-600 mt-2 font-body">{project.name}</p>
      </div>

      <div className="stat-card mb-8">
        <h3 className="font-heading font-bold text-xl mb-4 uppercase tracking-wide">Date Range Filter</h3>
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Start Date</label>
            <input
              type="date"
              data-testid="analytics-start-date"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">End Date</label>
            <input
              type="date"
              data-testid="analytics-end-date"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchAnalytics}
              data-testid="fetch-analytics-button"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center space-x-2"
            >
              <TrendingUp className="w-4 h-4" />
              <span>{loading ? 'Loading...' : 'Generate'}</span>
            </button>
          </div>
          <div className="flex items-end space-x-2">
            <button
              onClick={exportProjectSummary}
              data-testid="export-pdf-button"
              className="btn-secondary flex-1 flex items-center justify-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>PDF</span>
            </button>
            <button
              onClick={sendDailyReport}
              data-testid="send-email-button"
              className="btn-secondary flex-1 flex items-center justify-center space-x-2"
            >
              <Mail className="w-4 h-4" />
              <span>Email</span>
            </button>
          </div>
        </div>
      </div>

      {analytics && (
        <>
          <div className="stat-card mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-bold text-xl uppercase tracking-wide">Progress Timeline</h3>
              <span className="text-sm text-slate-600">{analytics.total_dpr_entries} DPR Entries</span>
            </div>
            {analytics.timeline.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={analytics.timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="planned" stroke="#0F172A" strokeWidth={2} name="Planned" />
                  <Line type="monotone" dataKey="actual" stroke="#10B981" strokeWidth={2} name="Actual" />
                  <Line type="monotone" dataKey="workforce" stroke="#F97316" strokeWidth={2} name="Workforce" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-slate-500">No data available for selected date range</div>
            )}
          </div>

          <div className="stat-card">
            <h3 className="font-heading font-bold text-xl mb-6 uppercase tracking-wide">Activity Breakdown</h3>
            {analytics.activities.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={analytics.activities}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="activity" tick={{ fontSize: 12 }} angle={-15} textAnchor="end" height={100} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="total_planned" fill="#0F172A" name="Total Planned" />
                  <Bar dataKey="total_actual" fill="#10B981" name="Total Actual" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-slate-500">No activities data available</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdvancedAnalytics;